import AbstractFormField from './AbstractFormField';
import Croppie from 'croppie';

class ProfilePhotoFormField extends AbstractFormField {
    acceptedTypes = new Set(['image/jpeg', 'image/png', 'image/webp']);
    maxFileSize = 8 * 1024 * 1024;
    maxWidth = 1024;
    minResolution = 512;
    cropTarget = null;
    error = null;
    hasImage = false;
    constructor(element, options) {
        super(element, { ...options, type: 'profile_photo' });

        this.input = element.querySelectorAll('input')[0];
        this.cropTarget = element.querySelectorAll('.cropper')[0];
        this.errorTarget = element.querySelectorAll('.photo-error')[0];
        this.requiredError = this.errorTarget.textContent.trim();
        this.existingValue = element.dataset.value;

        if (this.existingValue) {
            this.croppie = new Croppie(this.cropTarget, {
                enableResize: true,
                enableExif: true,
                viewport: {
                    width: 200,
                    height: 270,
                },
                boundary: {
                    width: 300,
                    height: 300,
                },
            });
            this.croppie.bind({
                url: this.existingValue,
            });
            this.hasImage = true;
            this.cropTarget.classList.add('active');
        } else {
            this.croppie = new Croppie(this.cropTarget, {
                enableResize: true,
                enableExif: true,
                viewport: {
                    width: 200,
                    height: 270,
                },
                boundary: {
                    width: 300,
                    height: 300,
                },
            });
        }

        const readFile = () => {
            const input = this.input;
            this.element.classList.remove('has-error');
            const that = this;
            if (input.files && input.files[0]) {
                const file = input.files[0];
                if (!this.acceptedTypes.has(file.type)) {
                    this.showError('Choose a JPG, PNG, or WebP image.');
                    input.value = '';
                    return;
                }
                if (file.size > this.maxFileSize) {
                    this.showError('Choose an image no larger than 8 MB.');
                    input.value = '';
                    return;
                }

                var reader = new FileReader();

                reader.onload = function (e) {
                    that.cropTarget.classList.add('active');
                    that.croppie
                        .bind({
                            url: e.target.result,
                        })
                        .then(function () {
                            that.hasImage = true;
                            that.resetError();
                        })
                        .catch(function () {
                            that.hasImage = false;
                            that.showError('We could not read this image. Please try another image.');
                        });
                };
                reader.readAsDataURL(file);
            } else {
            }
        };

        this.input.addEventListener('change', readFile.bind(this));
    }

    async getValue() {
        if (!this.hasImage) {
            return null;
        }

        const points = this.croppie.get().points;
        let width = Math.max(1, Math.floor(points[2] - points[0]));
        let height = Math.max(1, Math.floor(points[3] - points[1]));
        const scale = Math.min(1, this.maxWidth / width);
        width = Math.max(1, Math.floor(width * scale));
        height = Math.max(1, Math.floor(height * scale));

        let blob = await this.croppie.result({
            type: 'blob',
            size: { width, height },
            format: 'jpeg',
            quality: 0.9,
        });

        if (blob.size <= this.maxFileSize) {
            return blob;
        }

        while (blob.size > this.maxFileSize && width > 1 && height > 1) {
            const scale = Math.min(0.9, Math.sqrt(this.maxFileSize / blob.size) * 0.95);
            width = Math.max(1, Math.floor(width * scale));
            height = Math.max(1, Math.floor(height * scale));
            blob = await this.croppie.result({
                type: 'blob',
                size: { width, height },
                format: 'jpeg',
                quality: 0.9,
            });
        }

        return blob;
    }

    isValid() {
        if (!this.hasImage) {
            if (this.isRequired) {
                this.showError(this.requiredError);
            }
            return !this.isRequired;
        }

        const points = this.croppie.get().points;
        const width = Math.max(1, Math.floor(points[2] - points[0]));
        const height = Math.max(1, Math.floor(points[3] - points[1]));
        const scale = Math.min(1, this.maxWidth / width);
        if (Math.floor(width * scale) < this.minResolution || Math.floor(height * scale) < this.minResolution) {
            this.showError('The cropped photo must be at least 512 × 512 pixels. Zoom out or choose a larger image.');
            return false;
        }

        this.resetError();
        return true;
    }

    showError(message) {
        this.element.classList.add('has-error');
        this.errorTarget.textContent = message;
        this.errorTarget.classList.remove('hidden');
    }

    resetError() {
        this.element.classList.remove('has-error');
        this.errorTarget.classList.add('hidden');
    }
}

export default ProfilePhotoFormField;
